import { useState, useCallback } from "react";
import { Link } from "@remix-run/react";
import { Mail, Lock, Eye, EyeOff, ArrowRight, User, ShieldCheck } from "lucide-react";
import { SplashScreen } from "./SplashScreen";
import styles from "./AppAuthGate.module.css";

interface AppAuthGateProps {
  appName: string;
  appTag: string;
  onAuth: () => void;
}

type AuthMode = "login" | "register";

function getAuthKey(appName: string) {
  return `auth_gate_${appName.replace(/\s+/g, "_").toLowerCase()}`;
}

export function isAppAuthenticated(appName: string): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(getAuthKey(appName)) === "1";
}

function authenticate(appName: string) {
  if (typeof window === "undefined") return;
  localStorage.setItem(getAuthKey(appName), "1");
}

function logoutApp(appName: string) {
  if (typeof window === "undefined") return;
  localStorage.removeItem(getAuthKey(appName));
}

export function useAppAuth(appName: string) {
  const doLogout = useCallback(() => logoutApp(appName), [appName]);
  return { logout: doLogout };
}

export default function AppAuthGate({ appName, appTag, onAuth }: AppAuthGateProps) {
  const [phase, setPhase] = useState<"splash" | "auth">("splash");
  const [mode, setMode] = useState<AuthMode>("login");
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSplashComplete = useCallback(() => {
    setPhase("auth");
  }, []);

  const handleGuest = () => {
    authenticate(appName);
    onAuth();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (mode === "login") {
      if (!email.trim() || !password.trim()) {
        setError("Please enter your email and password.");
        return;
      }
    } else {
      if (!name.trim() || !email.trim() || !password.trim()) {
        setError("Please fill in all fields.");
        return;
      }
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }
      if (password !== confirm) {
        setError("Passwords do not match.");
        return;
      }
    }

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      authenticate(appName);
      onAuth();
    }, 400);
  };

  return (
    <>
      {phase === "splash" && (
        <SplashScreen name={appName} tag={appTag} onComplete={onSplashComplete} />
      )}

      {phase === "auth" && (
        <div className={styles.layout}>
          <div className={styles.card}>
            <div className={styles.header}>
              <div className={styles.headerIcon}>
                <ShieldCheck size={18} strokeWidth={1.5} />
              </div>
              <p className={styles.tag}>{appName} · Demo</p>
              <h1 className={styles.title}>
                {mode === "login" ? "Welcome back" : "Create account"}
              </h1>
              <p className={styles.subtitle}>
                {mode === "login"
                  ? "Sign in to access your workspace."
                  : "Join to unlock your tools and preferences."}
              </p>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit} className={styles.form}>
              {mode === "register" && (
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="name">Full Name</label>
                  <div className={styles.inputWrap}>
                    <span className={styles.icon}><User size={14} strokeWidth={1.5} /></span>
                    <input
                      id="name"
                      type="text"
                      className={styles.input}
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoComplete="name"
                      required
                    />
                  </div>
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label} htmlFor="email">Email</label>
                <div className={styles.inputWrap}>
                  <span className={styles.icon}><Mail size={14} strokeWidth={1.5} /></span>
                  <input
                    id="email"
                    type="email"
                    className={styles.input}
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="password">Password</label>
                <div className={styles.inputWrap}>
                  <span className={styles.icon}><Lock size={14} strokeWidth={1.5} /></span>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className={styles.input}
                    placeholder={mode === "register" ? "At least 8 characters" : "••••••••"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    required
                  />
                  <button
                    type="button"
                    className={styles.toggleBtn}
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              {mode === "register" && (
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="confirm">Confirm Password</label>
                  <div className={styles.inputWrap}>
                    <span className={styles.icon}><Lock size={14} strokeWidth={1.5} /></span>
                    <input
                      id="confirm"
                      type={showPassword ? "text" : "password"}
                      className={styles.input}
                      placeholder="Repeat your password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>
              )}

              <div className={styles.actions}>
                <button type="submit" className={styles.submitBtn} disabled={loading}>
                  {loading ? (
                    <span className={styles.spinnerWrap}>
                      <span className={styles.spinner} />
                      {mode === "login" ? "Signing in…" : "Creating…"}
                    </span>
                  ) : (
                    <>
                      {mode === "login" ? "Sign In" : "Create Account"}
                      <ArrowRight size={14} strokeWidth={2} />
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className={styles.divider}>
              <span className={styles.dividerLine} />
              <span className={styles.dividerText}>or</span>
              <span className={styles.dividerLine} />
            </div>

            <button type="button" className={styles.guestBtn} onClick={handleGuest}>
              Continue as Guest
            </button>

            <div className={styles.footerLinks}>
              <p className={styles.footerText}>
                {mode === "login" ? (
                  <>
                    Don&apos;t have an account?{" "}
                    <button type="button" className={styles.linkBtn} onClick={() => { setMode("register"); setError(""); }}>
                      Create one
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <button type="button" className={styles.linkBtn} onClick={() => { setMode("login"); setError(""); }}>
                      Sign in
                    </button>
                  </>
                )}
              </p>
            </div>
          </div>

          <Link to="/" className={styles.backLink}>
            ← Back to OS
          </Link>
        </div>
      )}
    </>
  );
}
